import { ApiEndpointHandler, createGetEndpoint, getQueryString } from '@sonar-software/sonar-http';
import { DataDictionaryField, DataDictionaryObject, DataDictionarySchema } from '@types';
import {
	DynamicQueryParams,
	KeyPaginationQueryParams,
	PaginatedResource,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';

export type DataDictionaryBaseRequest = KeyPaginationQueryParams & DynamicQueryParams & {
	viewId?: number;
};

export type DataDictionaryBaseResponse<T> = PaginatedResource<T, DataDictionarySchema> & {
	appliedColumns: string;
	appliedOrderingAndFilters: string;
};

export type GetDataDictionaryFieldsRequest = DataDictionaryBaseRequest & {
	sonarObjectId: number;
};

export type GetDataDictionaryByTypeRequest = DataDictionaryBaseRequest & {
	sonarObjectType: SonarObjectType;
};

export type GetDataDictionaryByApplicationRequest = DataDictionaryBaseRequest & {
	applicationId: number;
};

export type GetDataDictionaryByTagRequest = DataDictionaryBaseRequest & {
	tagId: number;
};

export type GetDataDictionaryByComplianceCategoryRequest = DataDictionaryBaseRequest & {
	complianceCategory: number;
};

export type GetDataDictionaryFieldsResponse = DataDictionaryBaseResponse<DataDictionaryField>;
export type GetDataDictionaryByTypeResponse = DataDictionaryBaseResponse<DataDictionaryObject>;

export type DataDictionaryRequest = GetDataDictionaryFieldsRequest |
	GetDataDictionaryByTypeRequest |
	GetDataDictionaryByApplicationRequest |
	GetDataDictionaryByTagRequest |
	GetDataDictionaryByComplianceCategoryRequest;
export type DataDictionaryResponse = GetDataDictionaryFieldsResponse |
	GetDataDictionaryByTypeResponse;

const getDataDictionaryFields = createGetEndpoint<GetDataDictionaryFieldsRequest, GetDataDictionaryFieldsResponse>(
	(request) => {
		const { sonarObjectId, ...rest } = request;

		return `/api/v3/data-dictionary/fields/${sonarObjectId}?${getQueryString(rest)}`;
	}
);

const getDataDictionaryByType = createGetEndpoint<GetDataDictionaryByTypeRequest, GetDataDictionaryByTypeResponse>(
	(request) => {
		const { sonarObjectType, ...rest } = request;

		return `/api/v3/data-dictionary/sonar-objects/types/${sonarObjectType}?${getQueryString(rest)}`;
	}
);

const getDataDictionaryByApplication = createGetEndpoint<
	GetDataDictionaryByApplicationRequest,
	GetDataDictionaryFieldsResponse
>(
	(request) => {
		const { applicationId, ...rest } = request;

		return `/api/v3/data-dictionary/applications/${applicationId}?${getQueryString(rest)}`;
	}
);

const getDataDictionaryByTag = createGetEndpoint<
	GetDataDictionaryByTagRequest,
	GetDataDictionaryFieldsResponse
>(
	(request) => {
		const { tagId, ...rest } = request;

		return `/api/v3/data-dictionary/tags/${tagId}?${getQueryString(rest)}`;
	}
);

const getDataDictionaryByComplianceCategory = createGetEndpoint<
	GetDataDictionaryByComplianceCategoryRequest,
	GetDataDictionaryFieldsResponse
>(
	(request) => {
		const { complianceCategory, ...rest } = request;

		return `/api/v3/data-dictionary/compliance-categories/${complianceCategory}?${getQueryString(rest)}`;
	}
);

export const getDataDictionary: ApiEndpointHandler<DataDictionaryRequest, DataDictionaryResponse> = (request) => {
	if ((request as GetDataDictionaryFieldsRequest).sonarObjectId) {
		return getDataDictionaryFields(request as GetDataDictionaryFieldsRequest);
	} else if ((request as GetDataDictionaryByTagRequest).tagId) {
		return getDataDictionaryByTag(request as GetDataDictionaryByTagRequest);
	} else if ((request as GetDataDictionaryByApplicationRequest).applicationId) {
		return getDataDictionaryByApplication(request as GetDataDictionaryByApplicationRequest);
	} else if (typeof (request as GetDataDictionaryByComplianceCategoryRequest).complianceCategory === 'number') {
		return getDataDictionaryByComplianceCategory(request as GetDataDictionaryByComplianceCategoryRequest);
	}

	return getDataDictionaryByType(request as GetDataDictionaryByTypeRequest);
};
